import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Lock, Eye, EyeOff, Shield, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivateSubAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [subAccount, setSubAccount] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setError('Token d\'activation manquant');
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/sub-accounts/verify-token/${token}`);
      setSubAccount(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Token invalide ou expiré');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setActivating(true);
    setError(null);
    
    try {
      await axios.post(`${API}/sub-accounts/activate`, {
        token,
        password: formData.password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivating(false);
    }
  };

  const getTypeLabel = (type) => {
    return type === 'delegue' ? 'Délégué' : 'Locataire';
  };

  const getTypeIcon = (type) => {
    return type === 'delegue' ? '🗳️' : '🏠';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-[#00D3A8] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Vérification du lien d'activation...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Compte activé !</h1>
          <p className="text-slate-600 mb-6">
            Votre compte a été activé avec succès. Vous pouvez maintenant vous connecter.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-3 px-6 rounded-xl font-medium transition-colors"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  if (error && !subAccount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Lien invalide</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500 mb-6">
            Ce lien d'activation est peut-être expiré ou a déjà été utilisé. 
            Contactez le coproprietaire pour recevoir une nouvelle invitation.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full border border-slate-300 text-slate-700 py-3 px-6 rounded-xl font-medium hover:bg-slate-50 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#00D3A8] to-[#0099CC] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-3xl">{getTypeIcon(subAccount?.type)}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Activez votre compte</h1>
          <p className="text-slate-600">
            Bienvenue sur SyndicAppli !
          </p>
        </div>

        {/* Account Info */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-semibold">
              {subAccount?.prenom?.[0]}{subAccount?.nom?.[0]}
            </div>
            <div>
              <p className="font-semibold text-slate-800">{subAccount?.prenom} {subAccount?.nom}</p>
              <p className="text-sm text-slate-500">{subAccount?.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Shield size={16} className="text-[#00D3A8]" />
            <span className="text-slate-600">Accès <strong>{getTypeLabel(subAccount?.type)}</strong></span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Créez votre mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                placeholder="Minimum 8 caractères"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Confirmez le mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                placeholder="Répétez le mot de passe"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Password strength indicator */}
          {formData.password && (
            <div className="space-y-2">
              <div className="flex space-x-1">
                <div className={`h-1 flex-1 rounded ${formData.password.length >= 8 ? 'bg-green-500' : 'bg-slate-200'}`}></div>
                <div className={`h-1 flex-1 rounded ${formData.password.length >= 10 ? 'bg-green-500' : 'bg-slate-200'}`}></div>
                <div className={`h-1 flex-1 rounded ${/[A-Z]/.test(formData.password) && /[0-9]/.test(formData.password) ? 'bg-green-500' : 'bg-slate-200'}`}></div>
              </div>
              <p className="text-xs text-slate-500">
                {formData.password.length < 8 ? 'Trop court' : 
                 formData.password.length < 10 ? 'Acceptable' : 
                 /[A-Z]/.test(formData.password) && /[0-9]/.test(formData.password) ? 'Fort' : 'Bon'}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={activating}
            className="w-full bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-3 px-6 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {activating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Activation en cours...</span>
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                <span>Activer mon compte</span>
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-6">
          En activant votre compte, vous acceptez les conditions d'utilisation de SyndicAppli.
        </p>
      </div>
    </div>
  );
};

export default ActivateSubAccount;
